/**
 * The List view of the WorkshopsCalendar card: the shared `WorkshopList`
 * cards for the workshops still to come, a page at a time.
 *
 * It is not a month. The calendar hands it every upcoming workshop in its
 * fixed window ahead (see `workshops-calendar-period`); this shows the first
 * page and a button for the next, until the window is used up.
 *
 * A caller that draws its own chips (Region schedule) is saying not every
 * workshop opens, and the list has no per-workshop slot to honour that with,
 * so only the viewer's own cards open.
 *
 * No inner scroll: the tag and the ring sit just outside each card's edge,
 * and an overflowing wrapper would clip them. The page scrolls instead.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';

import { WorkshopList } from './WorkshopList';
import { useWorkshopRenderSlot } from './workshops-calendar-render-context';
import { WorkshopsCalendarListFooter } from './WorkshopsCalendarListFooter';
import type { WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

export const LIST_PAGE_SIZE = 30;

const isMine = (w: WorkshopChip): boolean => Boolean(w.mine);

export interface WorkshopsCalendarListViewProps {
  /** The upcoming workshops to draw, in date order. */
  workshops: WorkshopChip[];
  emptyLabel?: string;
  /** Whether the cards carry the viewer's role tag; the ring shows either way. */
  showTag?: boolean;
  /** The last day the upcoming window covers, e.g. "Jan 5". */
  through: string;
  showPacing?: boolean;
  onWorkshopClick?: WorkshopClickHandler;
}

export function WorkshopsCalendarListView({
  workshops,
  emptyLabel = 'No upcoming workshops.',
  showTag,
  through,
  showPacing,
  onWorkshopClick,
}: WorkshopsCalendarListViewProps): ReactElement {
  // Back to the first page each time the List opens: the body unmounts this
  // view when another one is picked. Kept across legend changes on purpose.
  const [shown, setShown] = useState(LIST_PAGE_SIZE);
  const hasRenderSlot = Boolean(useWorkshopRenderSlot());

  return (
    <div style={{ flex: 1, padding: '12px 16px 16px' }}>
      <WorkshopList
        workshops={workshops.slice(0, shown)}
        size="lg"
        showPacing={showPacing}
        showTag={showTag}
        canOpen={hasRenderSlot ? isMine : undefined}
        onWorkshopClick={onWorkshopClick}
        emptyLabel={emptyLabel}
      />
      {workshops.length > 0 && (
        <WorkshopsCalendarListFooter
          shown={shown}
          total={workshops.length}
          through={through}
          onLoadMore={() => setShown((n) => n + LIST_PAGE_SIZE)}
        />
      )}
    </div>
  );
}
